import { useState, useMemo, useRef } from 'react'
import * as Yup from 'yup'
import clsx from 'clsx'
import { Link } from 'react-router'
import { useFormik } from 'formik'
import { resetPassword } from '../../../redux/auth/passwordSlice'
import { ResetPasswordRequestModel } from '../../../redux/auth/_authTypes'
import { AppDispatch } from '../../../redux/store'
import { useDispatch } from 'react-redux'
import ReCAPTCHA from 'react-google-recaptcha'
import { handleError } from '../../../utils/handleError'
import { FormattedMessage, useIntl } from 'react-intl'
import { useLang } from '../../../../_metronic/i18n/Metronici18n'

export function ForgotPassword() {
  const currentLang = useLang();
  const intl = useIntl();
  const dispatch = useDispatch<AppDispatch>();
  const [loading, setLoading] = useState(false);
  const [hasErrors, setHasErrors] = useState<boolean | undefined>(undefined);
  const [statusMessage, setStatusMessage] = useState<any | null>(null);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const recaptchaRef = useRef<ReCAPTCHA>(null);

  const forgotPasswordSchema = useMemo(() => Yup.object().shape({
    userName: Yup.string()
      .matches(/^(?:[a-zA-Z0-9_-]{1,50}|[^@\s]+@[^@\s]+\.[^@\s]+)$/, intl.formatMessage({ id: 'AUTH.VALIDATION.USER_NAME.INVALID' }))
      .required(intl.formatMessage({ id: 'AUTH.VALIDATION.USER_NAME.REQUIRED' })),
  }), [intl]);

  const formik = useFormik<ResetPasswordRequestModel>({
    initialValues: {
      userName: '',
      captchaToken: '',
    },
    validationSchema: forgotPasswordSchema,
    onSubmit: async (values) => {
      setLoading(true);
      setStatusMessage(null);
      setHasErrors(undefined);

      try {
        if (!captchaToken) {
          setFunctionValues(true, intl.formatMessage({ id: 'AUTH.GENERAL.RECAPTCHA_COMPLETE' }));
          return;
        }

        const response = await dispatch(resetPassword({ ...values, captchaToken })).unwrap();
        if (response === true) {
          setFunctionValues(false, intl.formatMessage({ id: 'AUTH.FORGOT.SUCCESS_RESET_EMAIL' }));
        } else {
          setFunctionValues(true, intl.formatMessage({ id: 'AUTH.FORGOT.ERROR' }));
        }
      } catch (error) {
        setFunctionValues(true, handleError(error));
      }
    },
  });

  function setFunctionValues(hasError: boolean, statusMessage: string) {
    setStatusMessage(statusMessage);
    setLoading(false);
    formik.setSubmitting(false);
    setHasErrors(hasError);
    recaptchaRef.current?.reset();
    setCaptchaToken(null);
  }

  return (
    <form
      className='form w-100 fv-plugins-bootstrap5 fv-plugins-framework'
      noValidate
      id='kt_login_password_reset_form'
      onSubmit={formik.handleSubmit}
    >
      <div className='text-center mb-10'>
        <h1 className='text-gray-900 fw-bolder mb-3'>
          <FormattedMessage id="AUTH.FORGOT.TITLE" />
        </h1>
        <div className='text-gray-500 fw-semibold fs-6'>
          <FormattedMessage id="AUTH.FORGOT.DESC" />
        </div>
      </div>

      {statusMessage && (
        <div className={`alert ${hasErrors === false ? 'alert-info' : 'alert-danger'}`}>
          <div className='alert-text font-weight-bold'>
            {statusMessage}
          </div>
        </div>
      )}

      <div className='fv-row mb-8'>
        <input
          type='text'
          placeholder={intl.formatMessage({ id: 'AUTH.GENERAL.USERNAME' })}
          autoComplete='off'
          {...formik.getFieldProps('userName')}
          className={clsx(
            'form-control bg-transparent text-uppercase',
            { 'is-invalid': formik.touched.userName && formik.errors.userName },
            { 'is-valid': formik.touched.userName && !formik.errors.userName },
          )}
        />
        {formik.touched.userName && formik.errors.userName && (
          <div className='fv-plugins-message-container'>
            <div className='fv-help-block'>
              <span role='alert'>{formik.errors.userName}</span>
            </div>
          </div>
        )}
      </div>

      <div className='fv-row mb-8 d-flex justify-content-center mb-4'>
        <ReCAPTCHA
          ref={recaptchaRef}
          sitekey={import.meta.env.VITE_TCGATE_RECAPTCHA_SITE_KEY}
          hl={currentLang}
          onChange={setCaptchaToken}
          onExpired={() => setCaptchaToken(null)}
          onErrored={() => setCaptchaToken(null)}
        />
      </div>

      <div className='d-flex flex-wrap justify-content-center pb-lg-0'>
        <button type='submit' className='btn btn-primary me-4' disabled={loading || !formik.isValid || !captchaToken}>
          <span className='indicator-label'>
            {loading ? <FormattedMessage id="GENERAL.PLEASE_WAIT" /> : <FormattedMessage id="GENERAL.SUBMIT_BUTTON" />}
          </span>
          {loading && (
            <span className='indicator-progress'>
              <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
            </span>
          )}
        </button>
        <Link to='/auth/login'>
          <button
            type='button'
            className='btn btn-light'
            disabled={loading}
          >
            <FormattedMessage id="GENERAL.CANCEL_BUTTON" />
          </button>
        </Link>
      </div>
    </form>
  );
}
